import { useState } from "react";
import { ApiError, NotAuthenticatedError, api, type AgentEnvelope } from "../api";
import { formatTime } from "../components";

/**
 * Ask the read-only agent why an instrument has not traded.
 *
 * The page is built around one idea: **the answer is shown next to what it
 * was made from.** Every reply carries the run ids it was derived from, the
 * model that actually answered, and the prompt version — so a claim can be
 * checked against the Signal/Audit record rather than believed.
 *
 * That is not decoration. On its first live run this agent stated a safety
 * property backwards (docs/07 §28), fluently and with high confidence. The
 * only reason it was caught is that the answer sat beside the records.
 */
export function Explain({ onSignedOut }: { onSignedOut: () => void }) {
  const [symbol, setSymbol] = useState("XAUUSD");
  const [question, setQuestion] = useState(
    "Why has the system not placed any trade on this instrument today?",
  );
  const [answer, setAnswer] = useState<AgentEnvelope | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function ask() {
    setBusy(true);
    setError(null);
    setAnswer(null);
    try {
      setAnswer(await api.explain(symbol, question));
    } catch (err) {
      if (err instanceof NotAuthenticatedError) {
        onSignedOut();
        return;
      }
      // A cold model takes minutes to start, and that is the normal first
      // request after an idle period rather than a fault — so it is worth
      // saying plainly instead of showing a bare error code.
      if (err instanceof ApiError && err.code === "AGENTS_NOT_CONFIGURED") {
        setError("No language model is configured for this deployment.");
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h1>Ask</h1>
      <p className="subtitle">
        A read-only agent that explains decisions the system already made and
        recorded. It cannot place, change or cancel anything.
      </p>

      <div className="card">
        <label>
          Instrument
          <input
            value={symbol}
            onChange={(e) => setSymbol(e.target.value.toUpperCase())}
            placeholder="XAUUSD"
          />
        </label>
        <label>
          Question
          <input
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            maxLength={500}
          />
        </label>
        <button
          disabled={busy || symbol.trim() === "" || question.trim() === ""}
          onClick={ask}
        >
          {busy ? "Asking…" : "Ask"}
        </button>
        {busy && (
          <p className="dim">
            If the model has been idle it is starting up, which takes several
            minutes. The first request after an idle period usually times out.
          </p>
        )}
        {error && <div className="notice error">{error}</div>}
      </div>

      {answer && <Answer envelope={answer} />}
    </>
  );
}

function Answer({ envelope }: { envelope: AgentEnvelope }) {
  const failed = typeof envelope.output.error === "string";

  return (
    <div className="card">
      <h2>
        Answer{" "}
        <span
          className={`badge ${
            envelope.confidence === "high"
              ? "ok"
              : envelope.confidence === "medium"
                ? ""
                : "warn"
          }`}
        >
          {envelope.confidence} confidence
        </span>{" "}
        {envelope.requires_human_review && (
          <span className="badge danger">needs review</span>
        )}
      </h2>

      {failed ? (
        // A failure is a recorded answer, not a blank. Saying "the AI did not
        // answer" is information; showing nothing is not.
        <div className="notice error">
          The agent could not answer: {String(envelope.output.error)}
        </div>
      ) : (
        <AnswerBody output={envelope.output} />
      )}

      {envelope.limitations.length > 0 && (
        <>
          <h3>What it could not tell</h3>
          <ul>
            {envelope.limitations.map((item) => (
              <li key={item} className="dim">
                {item}
              </li>
            ))}
          </ul>
        </>
      )}

      <h3>Where this came from</h3>
      <div className="metrics">
        <div className="metric">
          <div className="label">Model</div>
          <div className="value">{envelope.model}</div>
        </div>
        <div className="metric">
          <div className="label">Prompt</div>
          <div className="value">{envelope.prompt_version}</div>
        </div>
        <div className="metric">
          <div className="label">Evaluations used</div>
          <div className="value">{envelope.input_refs.length}</div>
        </div>
        <div className="metric">
          <div className="label">Answered at</div>
          <div className="value">{formatTime(envelope.created_at)}</div>
        </div>
      </div>
      <p className="dim">
        trace {envelope.trace_id} — this answer was built only from the
        evaluations listed above. Anything it states that is not in those
        records is not supported by the system.
      </p>
    </div>
  );
}

function AnswerBody({ output }: { output: Record<string, unknown> }) {
  const summary = typeof output.summary === "string" ? output.summary : null;
  const reasons = Array.isArray(output.reasons) ? output.reasons : [];
  const changes = Array.isArray(output.what_would_change_it)
    ? output.what_would_change_it
    : [];

  return (
    <>
      {summary && <p>{summary}</p>}

      {reasons.length > 0 && (
        <>
          <h3>Reasons</h3>
          <ul>
            {reasons.map((item, index) => (
              <li key={index}>{String(item)}</li>
            ))}
          </ul>
        </>
      )}

      {changes.length > 0 && (
        <>
          <h3>What would change it</h3>
          <ul>
            {changes.map((item, index) => (
              <li key={index}>{String(item)}</li>
            ))}
          </ul>
        </>
      )}

      {!summary && reasons.length === 0 && (
        // The agent's output shape is its own business; if it returned
        // something this page does not know how to lay out, show it rather
        // than silently dropping it.
        <pre className="dim">{JSON.stringify(output, null, 2)}</pre>
      )}
    </>
  );
}
